import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import { useLanguage } from '../../../contexts/LanguageContext';
import { HiOutlineExclamationTriangle, HiOutlineArrowPath, HiOutlineCube, HiOutlineMagnifyingGlass, HiOutlineInformationCircle } from 'react-icons/hi2';

// Column counts. `wide` is the no-sidebar layout (offers, bestsellers,
// campaigns), which has room for more.
export const gridColumns = (wide) =>
  wide
    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'
    : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4';

/** Placeholder card in the shape of ProductCard, for the loading state. */
export const ProductCardSkeleton = () => (
  <div className="overflow-hidden rounded-[1.4rem] bg-white ring-1 ring-black/[0.05] sm:rounded-[1.75rem]" aria-hidden="true">
    <div className="animate-pulse bg-[#F2EFEC] [aspect-ratio:1]" />
    <div className="px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3.5">
      <div className="h-3 w-full animate-pulse rounded-full bg-gray-100" />
      <div className="mt-2 h-3 w-2/3 animate-pulse rounded-full bg-gray-100" />
      <div className="mt-4 flex items-center justify-between">
        <div className="h-4 w-1/3 animate-pulse rounded-full bg-gray-100" />
        <div className="h-9 w-9 animate-pulse rounded-full bg-gray-100 sm:h-10 sm:w-10" />
      </div>
    </div>
  </div>
);

/** Empty / error state, on the landing page's off-white surface. */
export const GridMessage = ({ icon: Icon, title, text, children }) => (
  <div
    className="relative col-span-full overflow-hidden rounded-[2rem] px-6 py-14 text-center ring-1 ring-black/[0.04]"
    style={{ backgroundColor: '#F6F3F0' }}
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{ backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)', backgroundSize: '18px 18px' }}
    />
    <div className="relative mx-auto max-w-md">
      <span className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-black/[0.06] shadow-[0_14px_30px_-18px_rgb(var(--brand)/0.6)]">
        <Icon className="h-7 w-7" aria-hidden="true" />
      </span>
      <h3 className="text-xl font-extrabold tracking-[-0.02em] text-gray-900">{title}</h3>
      {text && <p className="mt-2 text-[15px] leading-relaxed text-gray-500">{text}</p>}
      {children}
    </div>
  </div>
);

const ProductGrid = ({
  wide = false,
  products,
  loading,
  error,
  favorite,
  didYouMean,
  onFavoriteClick,
  onProductClick,
  isLoggedIn
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleDidYouMeanClick = () => {
    if (didYouMean) {
      navigate(`/products?search=${encodeURIComponent(didYouMean)}`);
    }
  };

  if (loading) {
    return (
      <div className={`grid w-full gap-2.5 sm:gap-4 ${gridColumns(wide)}`} role="status" aria-label={t('products.grid.loading')}>
        {Array.from({ length: wide ? 10 : 8 }).map((_, i) => <ProductCardSkeleton key={i} />)}
      </div>
    );
  }

  if (error) {
    return (
      <GridMessage icon={HiOutlineExclamationTriangle} title={t('products.grid.unableToLoad')} text={error}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_28px_-12px_rgb(var(--brand)/0.55)] transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 landing:focus-visible:ring-brand/40 focus-visible:ring-offset-2"
        >
          <HiOutlineArrowPath className="h-4 w-4" aria-hidden="true" />
          {t('products.grid.tryAgain')}
        </button>
      </GridMessage>
    );
  }

  if (products.length === 0) {
    return (
      <GridMessage icon={HiOutlineCube} title={t('products.grid.noProductsAvailable')} text={t('products.grid.noProductsFound')}>
        {didYouMean && (
          <div className="mt-6 rounded-[1.5rem] bg-white p-5 ring-1 ring-black/[0.05]">
            <p className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600">
              <HiOutlineInformationCircle className="h-5 w-5 text-brand" aria-hidden="true" />
              {t('products.grid.didYouMean')}
            </p>
            <button
              type="button"
              onClick={handleDidYouMeanClick}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 landing:focus-visible:ring-brand/40 focus-visible:ring-offset-2"
            >
              <HiOutlineMagnifyingGlass className="h-4 w-4" aria-hidden="true" />
              "{didYouMean}"
            </button>
          </div>
        )}
      </GridMessage>
    );
  }

  return (
    <div className={`grid w-full gap-2.5 sm:gap-4 ${gridColumns(wide)}`}>
      {products.flatMap((product) => {
        if (!product || !product._id) return [];

        // Handle products with nested articles (generic products)
        if (product.articles && Array.isArray(product.articles)) {
          return product.articles.map((article, articleIndex) => {
            // Handle different image structures:
            // 1. Search results: article.images is directly an array
            // 2. Other cases: article.images.F is an array
            let imageArray = [];
            if (Array.isArray(article.images)) {
              imageArray = article.images;
            } else if (Array.isArray(article.images?.F) && article.images.F.length > 0) {
              imageArray = article.images.F;
            }

            const processedArticle = {
              pharmacode: article.pharmacode,
              description: {
                description: article.description?.description || 'No description available',
                longDescription: article.description?.longDescription || 'No description available'
              },
              hasPhoto: article.hasPhoto || false,
              images: {
                F: imageArray.map(img => img.replace(/^uploads[\\/]/, ''))
              },
              price: article.price || null,
              regulations: article.regulations || []
            };

            return (
              <ProductCard
                key={`${product._id}-${article.pharmacode}-${articleIndex}`}
                article={processedArticle}
                favorite={favorite}
                onFavoriteClick={onFavoriteClick}
                onProductClick={onProductClick}
                isPriority={false}
                isLoggedIn={isLoggedIn}
              />
            );
          });
        }

        // Handle regular products (search results return images as direct array)
        let regularImageArray = [];
        if (Array.isArray(product.images)) {
          regularImageArray = product.images;
        } else if (Array.isArray(product.images?.F) && product.images.F.length > 0) {
          regularImageArray = product.images.F;
        }

        const article = {
          pharmacode: product._id,
          description: {
            description: product.description?.description || 'No description available',
            longDescription: product.description?.longDescription || product.longDescription || 'No description available'
          },
          hasPhoto: product.hasPhoto || false,
          images: {
            F: regularImageArray.map(img => img.replace(/^uploads[\\/]/, ''))
          },
          price: product.price || product.prices || null,
          regulations: product.regulations || []
        };

        return (
          <ProductCard
            key={product._id}
            article={article}
            favorite={favorite}
            onFavoriteClick={onFavoriteClick}
            onProductClick={onProductClick}
            isPriority={false}
            isLoggedIn={isLoggedIn}
          />
        );
      })}
    </div>
  );
};

export default ProductGrid;
